// offline.ts — graceful-fallback UI for when the device is offline. Two surfaces:
//   1) showOfflineBanner(scene, msg?) — a small, dismissible top banner (wifiOff icon + a one-liner)
//      the scenes can drop in whenever a board falls back to a cached/seed board.
//   2) openOfflinePanel(scene, opts?) — the full "You're offline" explainer (matches redline __11) with
//      a PLAY OFFLINE primary + a RETRY CONNECTION secondary.
// Pure UI over the shared palette/kit + modal; no Core, no network, no determinism. The main thread
// wires the call sites (e.g. show the banner when boardClient serves a cached board).
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, secondaryButton, headingText, labelText, cardPanel } from './ui/kit';
import { icon, type IconName } from './icons';
import { scaledFont, motionAllowed } from './settings';
import { audio } from './audio';
import { isLandscape } from './stage';

const DEFAULT_BANNER = 'Offline — playing a cached board';
const DEFAULT_BODY = "No problem — you're playing the verified seed pack. Scores save on this device and sync when you reconnect.";
/** The load-bearing trust phrase, highlighted in `correct` green inside the body paragraph. */
const EMPHASIS = 'verified seed pack';

/** A colored run inside the body paragraph. */
interface Span {
  text: string;
  color: number;
}

/** Split the body copy into colored spans, emphasising the `verified seed pack` phrase in green. */
function bodySpans(message: string): Span[] {
  const i = message.indexOf(EMPHASIS);
  if (i < 0) return [{ text: message, color: PALETTE.dim }];
  return [
    { text: message.slice(0, i), color: PALETTE.dim },
    { text: EMPHASIS, color: PALETTE.correct },
    { text: message.slice(i + EMPHASIS.length), color: PALETTE.dim },
  ];
}

/**
 * Lay out a CENTERED multi-color paragraph with manual word-wrap (Phaser Text has no inline color
 * spans). Tokens flow left→right, wrap at `wrapW`, and each line is centered on `cx`. Returns the
 * paragraph's total pixel height so the caller can flow content beneath it.
 */
function centeredParagraph(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  cx: number,
  y: number,
  wrapW: number,
  spans: readonly Span[],
  bodyPx: number,
  lineH: number,
): number {
  // Tokenize: split on whitespace, remembering whether a space preceded each word (for wrap rules).
  interface Placed {
    t: Phaser.GameObjects.Text;
    w: number;
    space: boolean;
  }
  const spaceW = Math.round(bodyPx * 0.3);
  const lines: Placed[][] = [];
  let line: Placed[] = [];
  let lineW = 0;
  let pendingSpace = false;
  for (const span of spans) {
    for (const part of span.text.split(/(\s+)/)) {
      if (part === '') continue;
      if (/^\s+$/.test(part)) {
        pendingSpace = true;
        continue;
      }
      const t = scene.add
        .text(0, 0, part, { fontFamily: FONT.family, fontSize: `${bodyPx}px`, color: hex(span.color) })
        .setOrigin(0, 0.5);
      const w = t.width;
      const space = pendingSpace;
      pendingSpace = false;
      // Wrap only on a space-preceded token; glued punctuation never orphans.
      if (line.length > 0 && space && lineW + spaceW + w > wrapW) {
        lines.push(line);
        line = [{ t, w, space: false }];
        lineW = w;
      } else {
        line.push({ t, w, space: space && line.length > 0 });
        lineW += (space && line.length > 1 ? spaceW : 0) + w;
      }
    }
  }
  if (line.length) lines.push(line);

  // Position: center each line horizontally on cx.
  lines.forEach((ln, li) => {
    const lineY = y + li * lineH;
    let total = 0;
    ln.forEach((p, i) => {
      total += (i > 0 && p.space ? spaceW : 0) + p.w;
    });
    let curX = cx - total / 2;
    ln.forEach((p, i) => {
      if (i > 0 && p.space) curX += spaceW;
      p.t.setPosition(curX, lineY);
      parent.add(p.t);
      curX += p.w;
    });
  });
  return lines.length * lineH;
}

/**
 * Drop a small, dismissible offline banner at the top of the screen. Returns the Container so the
 * caller can `.destroy()` it when the connection returns (or let the user dismiss it via the ✕).
 */
export function showOfflineBanner(scene: Phaser.Scene, msg = DEFAULT_BANNER): Phaser.GameObjects.Container {
  const W = scene.scale.width;
  const w = Math.min(W * 0.92, 460);
  const h = 40;
  const y0 = 14 + h / 2; // just below the very top; sits under any safe-area inset the scene applies
  const c = scene.add.container(W / 2, y0).setDepth(25_000).setScrollFactor(0);

  const bg = scene.add.graphics();
  bg.fillStyle(PALETTE.surface, 1);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
  bg.lineStyle(1.25, PALETTE.armed, 0.7);
  bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
  c.add(bg);

  c.add(icon(scene, 'wifiOff', -w / 2 + 22, 0, 18, PALETTE.armed));
  const txt = scene.add
    .text(-w / 2 + 40, 0, msg, {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(Math.max(12, W * 0.024), 11)}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
    })
    .setOrigin(0, 0.5);
  c.add(txt);

  // ✕ dismiss (≥36 hit area).
  const xc = w / 2 - 18;
  const xIcon = icon(scene, 'x', xc, 0, 14, PALETTE.dim);
  const xHit = scene.add.rectangle(xc, 0, 36, 36, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  xHit.on('pointerup', () => {
    audio().playSfx('uiTap', { volume: 0.5 });
    c.destroy();
  });
  c.add([xIcon, xHit]);

  // Calm slide-in (gated for reduced-motion).
  if (motionAllowed()) {
    c.setY(y0 - 24).setAlpha(0);
    scene.tweens.add({ targets: c, y: y0, alpha: 1, duration: 200, ease: 'Cubic.easeOut' });
  }
  return c;
}

export interface OfflinePanelOpts {
  /** Override the body copy (defaults to the verified-seed-pack explainer). */
  message?: string;
  /** Tapping PLAY OFFLINE — defaults to just closing the panel. */
  onPlayOffline?: () => void;
  /** Tapping RETRY CONNECTION — defaults to a best-effort page reload. */
  onRetry?: () => void;
}

function statusRow(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  y: number,
  w: number,
  opts: { iconName: IconName; label: string; value: string; color: number },
): void {
  const row = cardPanel(scene, 0, y, w, 58, { fill: PALETTE.surface, border: PALETTE.line, radius: 12, accent: opts.color });
  const left = -w / 2 + 24;
  const disc = scene.add.circle(left + 13, 0, 15, opts.color, 0.12).setStrokeStyle(1.25, opts.color, 0.35);
  row.add([disc, icon(scene, opts.iconName, left + 13, 0, 16, opts.color)]);
  row.add(
    labelText(scene, left + 38, -10, opts.label, { size: 10, color: opts.color }).setOrigin(0, 0.5),
  );
  row.add(
    scene.add
      .text(left + 38, 12, opts.value, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.text),
        fontStyle: '600',
      })
      .setOrigin(0, 0.5),
  );
  parent.add(row);
}

/** Open the full "You're offline" explainer panel (graceful fallback). Returns the Modal handle. */
export function openOfflinePanel(scene: Phaser.Scene, opts: OfflinePanelOpts = {}): ReturnType<typeof modal> {
  const W = scene.scale.width;
  const desktop = isLandscape() && W >= 900;
  // Content-hugged, title-less system surface. It still reads like a recovery state, but no longer
  // burns half the phone screen on empty panel body.
  const m = modal(scene, {
    title: '',
    fullBleed: true,
    contentHeight: desktop ? 500 : 480,
    widthPx: desktop ? 700 : undefined,
    railGrad: [PALETTE.armed, PALETTE.correct],
  });
  const { card, cardW, cardH, footerY } = m;
  const top = -cardH / 2;

  // — amber wifiOff disc + compact status heading —
  const r = desktop ? 42 : 44;
  const discY = top + (desktop ? 78 : 82);
  const disc = scene.add.graphics();
  disc.fillStyle(PALETTE.armed, 0.1);
  disc.fillCircle(0, discY, r);
  disc.lineStyle(2, PALETTE.armed, 0.3);
  disc.strokeCircle(0, discY, r);
  card.add(disc);
  card.add(icon(scene, 'wifiOff', 0, discY, 38, PALETTE.armed));

  // — heading —
  card.add(headingText(scene, 0, discY + r + 34, "You're offline", { size: desktop ? 28 : 30 }));

  // — body (verified-seed-pack phrase emphasised in green), centered + wrapped —
  const bodyPx = scaledFont(17, 12);
  const bodyH = centeredParagraph(
    scene,
    card,
    0,
    discY + r + 78,
    Math.min(cardW - 76, desktop ? 520 : cardW - 54),
    bodySpans(opts.message ?? DEFAULT_BODY),
    bodyPx,
    Math.round(bodyPx * 1.5),
  );

  const rowW = Math.min(cardW - 52, desktop ? 560 : cardW - 48);
  const rowsTop = discY + r + 78 + bodyH + (desktop ? 36 : 34);
  statusRow(scene, card, rowsTop, rowW, {
    iconName: 'shield',
    label: 'VERIFIED PACK ACTIVE',
    value: 'Playable boards are already fact-checked.',
    color: PALETTE.correct,
  });
  statusRow(scene, card, rowsTop + 68, rowW, {
    iconName: 'clock',
    label: 'LOCAL SCORES SAVED',
    value: 'Your run stays on this device until sync.',
    color: PALETTE.scan,
  });

  // — actions: PLAY OFFLINE (primary) above RETRY CONNECTION (secondary), pinned to the compact footer —
  const btnW = rowW;
  const retryY = footerY;
  const retryH = 50;
  const playH = 58;
  const playY = retryY - (retryH / 2 + 14 + playH / 2);
  card.add(
    ctaButton(
      scene,
      0,
      playY,
      'Play Offline',
      () => {
        m.close();
        opts.onPlayOffline?.();
      },
      { width: btnW, height: playH, icon: 'play' },
    ),
  );
  card.add(
    secondaryButton(
      scene,
      0,
      retryY,
      'Retry Connection',
      () => {
        m.close();
        if (opts.onRetry) opts.onRetry();
        else
          try {
            window.location.reload();
          } catch {
            // headless / blocked — nothing to retry against.
          }
      },
      { width: btnW, height: retryH, icon: 'again' },
    ),
  );

  return m;
}
